import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FaSearch, FaFilter, FaEye, FaTimes } from "react-icons/fa";
import { ProgressBar } from "react-loader-spinner";
import { getTransactionList } from "../services/ClientProfileService";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { useTranslation } from "react-i18next";

const ClientTransactionList = () => {
    const { t } = useTranslation(["client"]);

    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const [transactionList, setTransactionList] = useState([
        {
            id: 1,
            botName: "ربات فروشگاه آنلاین",
            reasonKey: "subscription",
            date: "1405/05/12",
            amount: "2,500,000",
            gatewayKey: "zarinpal",
            paymentInfo: "ZP-123456",
            statusKey: "success"
        },
        {
            id: 2,
            botName: "ربات خدمات مشتریان",
            reasonKey: "subscription",
            date: "1405/05/11",
            amount: "1,200,000",
            gatewayKey: "idpay",
            paymentInfo: "PP-789012",
            statusKey: "pending"
        },
        {
            id: 3,
            botName: "ربات تحلیل داده",
            reasonKey: "financial",
            date: "1405/05/10",
            amount: "3,750,000",
            gatewayKey: "zarinpal",
            paymentInfo: "ZP-345678",
            statusKey: "failed"
        }
    ]);

    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    // Payment info modal
    const [selectedTransaction, setSelectedTransaction] = useState(null);

    const accessToken = useSelector((state) => state.auth.accessToken);

    useEffect(() => {
        const getList = async () => {
            try {
                setLoading(true);

                const res = await getTransactionList(accessToken);

                const transactions = res?.data?.data ?? [];

                const formattedTransactions = transactions.map((transaction) => {
                    const transactionDate = new Date(transaction.createdAt);

                    return {
                        id: transaction.id,

                        botName: transaction.business?.username ?? "-",

                        // ✅ کلید انگلیسی ذخیره می‌کنیم، نه متن ترجمه‌شده
                        reasonKey: transaction.planId
                            ? "subscription"
                            : transaction.offerCodeId
                                ? "discount"
                                : "financial",

                        date: transactionDate.toLocaleDateString("fa-IR"),

                        createdAt: new Date(transaction.createdAt).getTime(),

                        amount: transaction.paidAmount ?? transaction.amount ?? 0,

                        gatewayKey: transaction.ipg ?? null,

                        paymentInfo:
                            transaction.refId ??
                            transaction.trackId ??
                            null,

                        statusKey: transaction.status ?? null,
                    };
                });

                setTransactionList(formattedTransactions);

            } catch (err) {
                console.error("getTransactionList failed:", err);
                setTransactionList([]);
            } finally {
                setLoading(false);
            }
        };

        getList();
    }, [accessToken]);

    // وقتی جستجو یا فیلتر تغییر کرد، برگرد به صفحه اول
    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchTerm,
        filterStatus,
        fromDate,
        toDate
    ]);

    const getStatusColor = (statusKey) => {
        switch (statusKey) {
            case "success":
                return "bg-green-100 text-green-800 border-green-200 dark:!bg-green-950/60 dark:!text-green-300 dark:!border-green-800/70";

            case "pending":
                return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:!bg-yellow-950/60 dark:!text-yellow-300 dark:!border-yellow-800/70";

            case "failed":
                return "bg-red-100 text-red-800 border-red-200 dark:!bg-red-950/60 dark:!text-red-300 dark:!border-red-800/70";

            case "cancelled":
                return "bg-gray-100 text-gray-800 border-gray-200 dark:!bg-gray-800 dark:!text-gray-300 dark:!border-gray-700";

            default:
                return "bg-gray-100 text-gray-800 border-gray-200 dark:!bg-gray-800 dark:!text-gray-300 dark:!border-gray-700";
        }
    };

    const getStatusIcon = (statusKey) => {
        switch (statusKey) {
            case "success": return "✓";
            case "pending": return "⏳";
            case "failed": return "✗";
            case "cancelled": return "−";
            default: return "•";
        }
    };

    // Filter
    const filteredTransactions = transactionList.filter((transaction) => {
        const search = searchTerm.toLowerCase();

        const matchesSearch =
            transaction.botName?.toLowerCase().includes(search) ||
            transaction.reasonKey?.toLowerCase().includes(search) ||
            transaction.paymentInfo?.toLowerCase().includes(search);

        const matchesFilter =
            filterStatus === "all" ||
            transaction.statusKey === filterStatus;

        const transactionTime = transaction.createdAt;

        let matchesFromDate = true;
        let matchesToDate = true;

        if (fromDate) {
            const from = fromDate.toDate();
            from.setHours(0, 0, 0, 0);
            matchesFromDate = transactionTime >= from.getTime();
        }

        if (toDate) {
            const to = toDate.toDate();
            to.setHours(23, 59, 59, 999);
            matchesToDate = transactionTime <= to.getTime();
        }

        return (
            matchesSearch &&
            matchesFilter &&
            matchesFromDate &&
            matchesToDate
        );
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    const goToPreviousPage = () => {
        if (currentPage > 1) setCurrentPage((prev) => prev - 1);
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
    };

    const openPaymentModal = (transaction) => setSelectedTransaction(transaction);
    const closePaymentModal = () => setSelectedTransaction(null);

    const formatAmount = (amount) => Number(amount).toLocaleString("en-US");

    return (
        <>
            {loading ? (
                <div className="flex justify-center items-center h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:!from-gray-950 dark:!to-gray-900">
                    <ProgressBar
                        visible={true}
                        height="80"
                        width="80"
                        color="#6366f1"
                        ariaLabel="progress-bar-loading"
                        wrapperStyle={{}}
                        wrapperClass=""
                    />
                </div>
            ) : (
                <div className="min-h-screen bg-gray-50 dark:!bg-gray-950 transition-colors duration-300">
                    <div className="container mx-auto px-4 py-8">
                        {/* Filters Section */}
                        <div className="bg-white dark:!bg-gray-900 rounded-2xl shadow-lg dark:!shadow-black/40 p-4 mb-6 border border-gray-100 dark:!border-gray-800">
                            {/* Search */}
                            <div className="relative mb-4 sm:col-span-2 xl:col-span-1">
                                <FaSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:!text-gray-500 pointer-events-none" />

                                <input
                                    type="text"
                                    placeholder={t("transactions.search")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full h-11 pr-10 pl-4 bg-gray-50 dark:!bg-gray-800 border border-gray-200 dark:!border-gray-700 rounded-xl text-sm text-gray-800 dark:!text-gray-100 placeholder:text-gray-400 dark:!placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:!focus:ring-indigo-600 focus:border-transparent transition-all duration-200"
                                />
                            </div>
                            <div className="grid w-full sm:grid-cols-2 xl:grid-cols-5 gap-3">
                                {/* Status */}
                                <div className="relative">
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="w-full h-11 pr-9 pl-4 bg-gray-50 dark:!bg-gray-800 border border-gray-200 dark:!border-gray-700 rounded-xl text-sm text-gray-800 dark:!text-gray-100 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:!focus:ring-indigo-600 focus:border-transparent transition-all duration-200"
                                    >
                                        <option value="all">{t("transactions.allStatuses")}</option>
                                        <option value="success">{t("transactions.status.success")}</option>
                                        <option value="pending">{t("transactions.status.pending")}</option>
                                        <option value="failed">{t("transactions.status.failed")}</option>
                                        <option value="cancelled">{t("transactions.status.cancelled")}</option>
                                    </select>
                                </div>

                                {/* From Date */}
                                <div className="min-w-0">
                                    <DatePicker
                                        value={fromDate}
                                        onChange={setFromDate}
                                        calendar={persian}
                                        locale={persian_fa}
                                        calendarPosition="bottom-right"
                                        format="YYYY/MM/DD"
                                        placeholder={t("transactions.fromDate")}
                                        inputClass="!w-full !h-11 !px-3 !bg-gray-50 !border !border-gray-200 !rounded-xl !text-sm !text-center focus:!outline-none focus:!ring-2 focus:!ring-indigo-500 dark:!!bg-gray-800 dark:!!border-gray-700 dark:!!text-gray-100"
                                    />
                                </div>

                                {/* To Date */}
                                <div className="min-w-0">
                                    <DatePicker
                                        value={toDate}
                                        onChange={setToDate}
                                        calendar={persian}
                                        locale={persian_fa}
                                        calendarPosition="bottom-right"
                                        format="YYYY/MM/DD"
                                        placeholder={t("transactions.toDate")}
                                        inputClass="!w-full !h-11 !px-3 !bg-gray-50 !border !border-gray-200 !rounded-xl !text-sm !text-center focus:!outline-none focus:!ring-2 focus:!ring-indigo-500 dark:!!bg-gray-800 dark:!!border-gray-700 dark:!!text-gray-100"
                                    />
                                </div>

                                {/* Clear Filters */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchTerm("");
                                        setFilterStatus("all");
                                        setFromDate(null);
                                        setToDate(null);
                                        setCurrentPage(1);
                                    }}
                                    className="h-11 w-full inline-flex items-center justify-center gap-2 px-4 bg-gray-100 dark:!bg-gray-800 text-gray-600 dark:!text-gray-300 border border-gray-200 dark:!border-gray-700 hover:bg-red-500 dark:!hover:bg-red-600 hover:text-white hover:border-red-200 dark:!hover:border-red-500 rounded-xl text-sm font-medium transition-all duration-200"
                                >
                                    <FaTimes className="text-xs" />
                                    {t("transactions.clearFilters")}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Table Section */}
                    <div className="bg-white dark:!bg-gray-900 rounded-2xl shadow-lg dark:!shadow-black/40 overflow-hidden border border-gray-100 dark:!border-gray-800">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:!from-indigo-950/40 dark:!to-purple-950/40 border-b-2 border-indigo-100 dark:!border-indigo-900/60">
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:!text-gray-300 uppercase tracking-wider">
                                            {t("transactions.columns.row")}
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:!text-gray-300 uppercase tracking-wider">
                                            {t("transactions.columns.botName")}
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:!text-gray-300 uppercase tracking-wider">
                                            {t("transactions.columns.reason")}
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:!text-gray-300 uppercase tracking-wider">
                                            {t("transactions.columns.date")}
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:!text-gray-300 uppercase tracking-wider">
                                            {t("transactions.columns.amount")}
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:!text-gray-300 uppercase tracking-wider">
                                            {t("transactions.columns.gateway")}
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:!text-gray-300 uppercase tracking-wider">
                                            {t("transactions.columns.paymentInfo")}
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:!text-gray-300 uppercase tracking-wider">
                                            {t("transactions.columns.status")}
                                        </th>
                                    </tr>
                                </thead>

                                <tbody className="divide-y divide-gray-100 dark:!divide-gray-800">
                                    {currentTransactions.length > 0 ? (
                                        currentTransactions.map((transaction, index) => (
                                            <tr
                                                key={transaction.id}
                                                className="hover:bg-indigo-50/50 dark:!hover:bg-indigo-950/30 transition-all duration-200 group"
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:!text-gray-300 font-medium">
                                                    {startIndex + index + 1}
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-sm font-medium text-gray-700 dark:!text-gray-200">
                                                            {transaction.botName}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:!text-gray-400">
                                                    {t(`transactions.reason.${transaction.reasonKey}`)}
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:!text-gray-400">
                                                    {transaction.date}
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm font-bold text-teal-700 dark:!text-teal-400">
                                                        {formatAmount(transaction.amount)}{" "}
                                                        {t("transactions.currency")}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="px-3 py-1 bg-gray-100 dark:!bg-gray-800 text-gray-700 dark:!text-gray-300 rounded-full text-xs font-medium">
                                                        {transaction.gatewayKey
                                                            ? t(`transactions.gateway.${transaction.gatewayKey}`)
                                                            : "-"}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {transaction.paymentInfo ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => openPaymentModal(transaction)}
                                                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 dark:!bg-indigo-950/50 text-indigo-600 dark:!text-indigo-300 border border-indigo-100 dark:!border-indigo-900/70 rounded-lg text-xs font-medium hover:bg-indigo-100 dark:!hover:bg-indigo-950/70 hover:text-indigo-700 dark:!hover:text-indigo-200 transition-all duration-200"
                                                        >
                                                            <FaEye className="text-xs" />
                                                            {t("transactions.view")}
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 dark:!text-gray-500">
                                                            {t("transactions.noTrackingCode")}
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(transaction.statusKey)} inline-flex items-center gap-1`}
                                                    >
                                                        <span>{getStatusIcon(transaction.statusKey)}</span>
                                                        {transaction.statusKey
                                                            ? t(`transactions.status.${transaction.statusKey}`)
                                                            : "-"}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan="8"
                                                className="px-6 py-12 text-center text-gray-500 dark:!text-gray-400"
                                            >
                                                <div className="flex flex-col items-center gap-2">
                                                    <svg
                                                        className="w-12 h-12 text-gray-300 dark:!text-gray-600"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                                                        />
                                                    </svg>
                                                    <span className="text-sm">
                                                        {t("transactions.noTransactions")}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Table Footer */}
                        <div className="px-6 py-4 bg-gray-50 dark:!bg-gray-800/60 border-t border-gray-100 dark:!border-gray-800 flex items-center justify-between">
                            <span className="text-sm text-gray-600 dark:!text-gray-400">
                                {t("transactions.showing", {
                                    from: filteredTransactions.length === 0 ? 0 : startIndex + 1,
                                    to: Math.min(endIndex, filteredTransactions.length),
                                    total: filteredTransactions.length
                                })}
                            </span>

                            {totalPages > 0 && (
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={goToPreviousPage}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1.5 text-sm bg-white dark:!bg-gray-900 text-gray-700 dark:!text-gray-300 border border-gray-300 dark:!border-gray-700 rounded-lg hover:bg-gray-50 dark:!hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {t("transactions.previous")}
                                    </button>

                                    <span className="px-3 py-1.5 text-sm bg-teal-600 dark:!bg-teal-700 text-white rounded-lg">
                                        {t("transactions.pageOf", { current: currentPage, total: totalPages })}
                                    </span>

                                    <button
                                        type="button"
                                        onClick={goToNextPage}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1.5 text-sm bg-white dark:!bg-gray-900 text-gray-700 dark:!text-gray-300 border border-gray-300 dark:!border-gray-700 rounded-lg hover:bg-gray-50 dark:!hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {t("transactions.next")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Info Modal */}
            {selectedTransaction && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:!bg-black/70 backdrop-blur-sm px-4"
                    onClick={closePaymentModal}
                >
                    <div
                        className="w-full max-w-md bg-white dark:!bg-gray-900 rounded-2xl shadow-2xl dark:!shadow-black/60 border border-gray-100 dark:!border-gray-800 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:!border-gray-800 bg-gradient-to-r from-indigo-50 to-purple-50 dark:!from-indigo-950/40 dark:!to-purple-950/40">
                            <div>
                                <h3 className="text-base font-bold text-gray-800 dark:!text-gray-100">
                                    {t("transactions.paymentModal.title")}
                                </h3>
                                <p className="text-xs text-gray-500 dark:!text-gray-400 mt-1">
                                    {selectedTransaction.botName}
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={closePaymentModal}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:!text-gray-500 hover:text-gray-700 dark:!hover:text-gray-200 hover:bg-white dark:!hover:bg-gray-800 transition-all"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5">
                            <div className="bg-gray-50 dark:!bg-gray-800/60 border border-gray-200 dark:!border-gray-700 rounded-xl p-4">
                                <p className="text-xs text-gray-500 dark:!text-gray-400 mb-2">
                                    {t("transactions.paymentModal.trackingCode")}
                                </p>

                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-sm font-bold text-gray-800 dark:!text-gray-100 break-all">
                                        {selectedTransaction.paymentInfo}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 dark:!bg-gray-800/60 rounded-xl p-3">
                                    <p className="text-xs text-gray-400 dark:!text-gray-500 mb-1">
                                        {t("transactions.paymentModal.gateway")}
                                    </p>
                                    <p className="text-sm font-medium text-gray-700 dark:!text-gray-200">
                                        {selectedTransaction.gatewayKey
                                            ? t(`transactions.gateway.${selectedTransaction.gatewayKey}`)
                                            : "-"}
                                    </p>
                                </div>

                                <div className="bg-gray-50 dark:!bg-gray-800/60 rounded-xl p-3">
                                    <p className="text-xs text-gray-400 dark:!text-gray-500 mb-1">
                                        {t("transactions.paymentModal.amount")}
                                    </p>
                                    <p className="text-sm font-medium text-teal-700 dark:!text-teal-400">
                                        {formatAmount(selectedTransaction.amount)}{" "}
                                        {t("transactions.currency")}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-5 py-3 bg-gray-50 dark:!bg-gray-800/60 border-t border-gray-100 dark:!border-gray-800 flex justify-end">
                            <button
                                type="button"
                                onClick={closePaymentModal}
                                className="px-4 py-2 text-sm bg-white dark:!bg-gray-900 border border-gray-300 dark:!border-gray-700 rounded-lg text-gray-700 dark:!text-gray-200 hover:bg-gray-100 dark:!hover:bg-gray-800 transition-colors"
                            >
                                {t("transactions.paymentModal.close")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClientTransactionList;